/**
 * ProtectedRoute - Route guard component
 * Redirects to login if not authenticated
 */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Define role groups for convenience
export const FULL_ADMIN_ROLES = ['Admin', 'SuperAdmin'];
export const STAFF_ROLES = ['SuperAgent', 'Agent', 'Caissier'];
export const ADMIN_ROLES = [...FULL_ADMIN_ROLES, ...STAFF_ROLES];
export const USER_ROLES = ['User'];

interface ProtectedRouteProps {
    children: React.ReactNode;
    requiredRoles?: string[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
    children,
    requiredRoles,
}) => {
    const { isAuthenticated, isLoading, user } = useAuth();
    const location = useLocation();

    // Show loading while checking auth
    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }

    // Redirect to login if not authenticated
    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Check role if required
    if (requiredRoles && user && !requiredRoles.includes(user.role)) {
        // Redirect to appropriate page based on their actual role
        if (USER_ROLES.includes(user.role)) {
            return <Navigate to="/home" replace />;
        } else {
            return <Navigate to="/dashboard" replace />;
        }
    }

    return <>{children}</>;
};

/**
 * PublicRoute - Only accessible when NOT authenticated
 * Redirects based on role if already logged in
 */
export const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isAuthenticated, isLoading, user } = useAuth();

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (isAuthenticated && user) {
        // Redirect based on role
        if (user.role === 'User') {
            return <Navigate to="/home" replace />;
        } else {
            return <Navigate to="/dashboard" replace />;
        }
    }

    return <>{children}</>;
};

export default ProtectedRoute;

